import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useGetIdentity, useNotify } from "react-admin";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import Parse from "parse";

export const Marketing = () => {
  const { data: identity } = useGetIdentity();
  const notify = useNotify();
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isSuperUser = identity?.userRoleName === "Super-User";

  useEffect(() => {
    if (isSuperUser) {
      fetchImages();
    } else {
      setLoading(false);
    }
  }, [isSuperUser]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const result = await Parse.Cloud.run("getMarketingImages");
      setImages(result || []);
    } catch (error) {
      console.error("Error fetching images:", error);
      notify("Failed to fetch images", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGoogleDrive = () => {
    window.open(
      "https://drive.google.com/drive/folders/15g3uuvUb8ccpItgKS7vTErsZo2ynmv5b?usp=drive_link",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        errors.push(`${file.name}: Not an image file`);
        return;
      }
      // Validate file size (max 512KB)
      if (file.size > 512 * 1024) {
        errors.push(`${file.name}: Exceeds 512KB limit`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      notify(errors.join(", "), { type: "error" });
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      notify(`${validFiles.length} file(s) selected`, { type: "info" });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      notify("Please select at least one file", { type: "error" });
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of selectedFiles) {
        try {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          await Parse.Cloud.run("uploadMarketingImage", {
            fileName: file.name,
            fileBase64: base64Data,
            fileType: file.type,
          });

          successCount++;
        } catch (error) {
          console.error(`Upload error for ${file.name}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        notify(`${successCount} image(s) uploaded successfully`, { type: "success" });
      }
      if (errorCount > 0) {
        notify(`${errorCount} image(s) failed to upload`, { type: "error" });
      }

      setSelectedFiles([]);
      document.getElementById("file-upload-input").value = "";
      fetchImages();
    } catch (error) {
      console.error("Upload error:", error);
      notify("Failed to upload images", { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (imageUrl) => {
    setImageToDelete(imageUrl);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;

    setDeleting(true);
    try {
      await Parse.Cloud.run("deleteMarketingImage", {
        imageUrl: imageToDelete,
      });
      notify("Image deleted successfully", { type: "success" });
      setDeleteDialogOpen(false);
      setImageToDelete(null);
      fetchImages();
    } catch (error) {
      console.error("Delete error:", error);
      notify(error.message || "Failed to delete image", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  const handleClearSelection = () => {
    setSelectedFiles([]);
    document.getElementById("file-upload-input").value = "";
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0) {
      document.getElementById("file-upload-input").value = "";
    }
  };

  return (
    <Box sx={{ p: 3 }}>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: "#1C1C1E", height: "100%" }}>
            <CardContent>
              
              <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.7)" }}>
                Access marketing materials and resources
              </Typography>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenGoogleDrive}
                fullWidth
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#FFA000" },
                }}
              >
                Open Link
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {isSuperUser && (
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#1C1C1E", height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: "#fff" }}>
                  Upload Marketing Images
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.7)" }}>
                  Upload images for carousel display (Max 512KB each, PNG/JPG/JPEG/WebP)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <input
                    id="file-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="file-upload-input">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        borderColor: "#FFC107",
                        color: "#FFC107",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#FFA000",
                          backgroundColor: "rgba(255, 193, 7, 0.08)",
                        },
                      }}
                    >
                      Select Images
                    </Button>
                  </label>
                </Box>
                {selectedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box 
                      sx={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center",
                        mb: 1 
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        {selectedFiles.length} file(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleClearSelection}
                        sx={{ 
                          color: "#FFC107",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "rgba(255, 193, 7, 0.08)" }
                        }}
                      >
                        Clear All
                      </Button>
                    </Box>
                    {selectedFiles.map((file, index) => (
                      <Alert
                        key={index}
                        severity="info"
                        sx={{ mb: 1 }}
                        action={
                          <IconButton
                            aria-label="remove"
                            color="primary"
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            sx={{
                              color: "#0288d1",
                              "&:hover": {
                                backgroundColor: "rgba(2, 136, 209, 0.08)",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        {file.name}
                      </Alert>
                    ))}
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                  fullWidth
                  startIcon={
                    uploading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  sx={{
                    background: "#FFC107",
                    color: "#0A0A0A",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#FFA000" },
                    "&:disabled": {
                      backgroundColor: "rgba(255, 193, 7, 0.3)",
                      color: "rgba(10, 10, 10, 0.5)",
                    },
                  }}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {isSuperUser && (
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#1C1C1E" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: "#fff", mb: 2 }}>
                  Uploaded Images
                </Typography>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress sx={{ color: "#FFC107" }} />
                  </Box>
                ) : images.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", textAlign: "center", py: 3 }}>
                    No images uploaded yet
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {images.map((image, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card sx={{ backgroundColor: "#2C2C2E", position: "relative" }}>
                          <CardMedia
                            component="img"
                            
                            image={image.imageUrl}
                            alt={`Marketing image ${index + 1}`}
                            sx={{ objectFit: "contain", backgroundColor: "#1a1a1a" }}
                          />
                          <IconButton
                            onClick={() => handleDeleteClick(image.imageUrl)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              backgroundColor: "rgba(0, 0, 0, 0.6)",
                              color: "#ff4444",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.8)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            backgroundColor: "#1C1C1E",
            color: "#fff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Delete Image</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{
              color: "rgba(255,255,255,0.7)",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{
              background: "#ff4444",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#cc0000" },
              "&:disabled": {
                backgroundColor: "rgba(255, 68, 68, 0.3)",
                color: "rgba(255, 255, 255, 0.5)",
              },
            }}
            startIcon={deleting && <CircularProgress size={16} color="inherit" />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
